// ===========================================
// View Student Dialog Component
// ===========================================

import { getStudentAgeGroup } from '../../../api/users.api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Chip,
  Divider,
  Stack,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  MusicNote as MusicNoteIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { LessonEnrollment, getDayName, formatTime } from '../../../api/lessons.api';
import { StudentEnrollment } from '../../../api/users.api';
import { DEFAULT_AGE_GROUP_COLOR } from './lessonDetail.constants';
import { familyContactsFor } from '../../../utils/familyContacts';

interface ViewStudentDialogProps {
  open: boolean;
  onClose: () => void;
  student: LessonEnrollment['Student'] | null;
  enrollments: StudentEnrollment[] | undefined;
  isLoadingEnrollments: boolean;
}

export default function ViewStudentDialog({
  open,
  onClose,
  student,
  enrollments,
  isLoadingEnrollments,
}: ViewStudentDialogProps) {
  if (!student) return null;
  const contacts = familyContactsFor(student);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Student Details</DialogTitle>
      <DialogContent>
        <Box>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Name
              </Typography>
              <Typography>
                {student.firstName} {student.lastName}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Age Group
              </Typography>
              <Box>
                {getStudentAgeGroup(student) ? (
                  <Chip
                    label={getStudentAgeGroup(student)!.name}
                    size="small"
                    sx={{
                      backgroundColor: getStudentAgeGroup(student)!.color || DEFAULT_AGE_GROUP_COLOR,
                      color: 'white',
                    }}
                  />
                ) : (
                  <Typography color="text.secondary">—</Typography>
                )}
              </Box>
            </Grid>
            {student.birthDate && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Birth Date
                </Typography>
                <Typography>
                  {new Date(student.birthDate).toLocaleDateString()}
                </Typography>
              </Grid>
            )}
            {student.family && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Family
                </Typography>
                <Typography>{student.family.name}</Typography>
              </Grid>
            )}
            {student.notes && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Notes
                </Typography>
                <Typography>{student.notes}</Typography>
              </Grid>
            )}
          </Grid>

          {/* Parents — the family's contacts (names always; phone/email only
              when the API returned them, i.e. the viewer holds people_families:read) */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Parents
          </Typography>
          {contacts.length > 0 ? (
            <Grid container spacing={2}>
              {contacts.map((c) => (
                <Grid item xs={12} sm={6} key={c.name}>
                  <Typography>
                    {c.name}
                    {c.relationship && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' '}({c.relationship})
                      </Typography>
                    )}
                  </Typography>
                  {c.phone && (
                    <Typography variant="body2">
                      <Box component="a" href={`tel:${c.phone}`} sx={{ color: 'inherit' }}>
                        {c.phone}
                      </Box>
                    </Typography>
                  )}
                  {c.email && (
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      <Box component="a" href={`mailto:${c.email}`} sx={{ color: 'inherit' }}>
                        {c.email}
                      </Box>
                    </Typography>
                  )}
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No parent contact
            </Typography>
          )}

          {/* Class Enrollments */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Class Enrollments ({enrollments?.length || 0})
          </Typography>

          {isLoadingEnrollments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : enrollments && enrollments.length > 0 ? (
            <Stack spacing={1}>
              {enrollments.map((enrollment) => (
                <Paper
                  key={enrollment.id}
                  variant="outlined"
                  sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {enrollment.lesson.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {enrollment.lesson.instrument && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <MusicNoteIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                        <Typography variant="caption" color="text.secondary">
                          {enrollment.lesson.instrument}
                        </Typography>
                      </Box>
                    )}
                    {enrollment.lesson.teacher && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {enrollment.lesson.teacher}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {enrollment.lesson.dayOfWeek != null
                          ? `${getDayName(enrollment.lesson.dayOfWeek)} ${formatTime(enrollment.lesson.startTime!)} - ${formatTime(enrollment.lesson.endTime!)}`
                          : 'Schedule TBD'}
                      </Typography>
                    </Box>
                    {enrollment.lesson.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {enrollment.lesson.location}
                          {enrollment.lesson.room && ` - ${enrollment.lesson.room}`}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not enrolled in any classes
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
